import { SectionCard } from './ui/SectionCard';
import { useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  Legend,
} from 'recharts';
import type { Snapshot } from '@t212/shared';
import { fmtMoney, fmtMoneyCompact, fmtDate, fmtSignedPercent } from '@/lib/format';
import { cn } from '@/lib/cn';

type Props = {
  snapshots: Snapshot[];
};

// Synthetic baseline for demo mode. NOT a real index. In live mode this would
// be wired to a real series (Finnhub/Yahoo). Labelled explicitly as "Synthetic
// baseline (demo)" in the UI so it cannot be confused with FTSE-100 or any
// other actual benchmark.
function demoBaseline(snapshots: Snapshot[], anchorPct = 0): number[] {
  if (snapshots.length === 0) return [];
  let s = 0xcafe;
  const r = () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0x100000000;
  };
  const out: number[] = [];
  let idx = 100;
  for (let i = 0; i < snapshots.length; i += 1) {
    const noise = (r() * 2 - 1) * 0.6;
    const drift = 0.05;
    idx *= 1 + (drift + noise) / 100;
    out.push(idx + anchorPct);
  }
  return out;
}

export function EquityCurve({ snapshots }: Props) {
  const [showBenchmark, setShowBenchmark] = useState(true);

  const data = useMemo(() => {
    const benchSeries = demoBaseline(snapshots);
    const portfolioBase = snapshots[0]?.totalValue ?? 1;
    return snapshots.map((s, i) => ({
      time: s.takenAt,
      value: Math.round(s.totalValue),
      // Indexed % return from start, for the benchmark overlay
      portfolioPct: portfolioBase > 0 ? ((s.totalValue - portfolioBase) / portfolioBase) * 100 : 0,
      benchmarkPct: (benchSeries[i] ?? 100) - 100,
    }));
  }, [snapshots]);

  const peak = useMemo(
    () => snapshots.reduce((m, s) => Math.max(m, s.totalValue), 0),
    [snapshots],
  );

  const portfolioReturn = data[data.length - 1]?.portfolioPct ?? 0;

  return (
    <SectionCard>
      <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-medium text-zinc-200">Equity curve</h3>
          <p className="text-[11px] text-zinc-400">
            90 days · You {fmtSignedPercent(portfolioReturn)}
            {showBenchmark && (
              <span title="Synthetic baseline. Not a real index — wire a real series in live mode.">
                {' '}· vs synthetic baseline (demo)
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowBenchmark((v) => !v)}
            className={cn(
              'text-[11px] px-2 py-1 rounded-md border transition-colors',
              showBenchmark
                ? 'border-indigo-500/40 bg-indigo-500/10 text-indigo-200'
                : 'border-zinc-800 text-zinc-400 hover:text-zinc-200',
            )}
          >
            Benchmark
          </button>
          <span className="text-[11px] text-zinc-400">Peak {fmtMoneyCompact(peak)}</span>
        </div>
      </div>
      <div className="h-56 -ml-2">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
            <defs>
              <linearGradient id="eqg" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#6366f1" stopOpacity={0.5} />
                <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="time"
              tickFormatter={(v) => fmtDate(v).slice(0, 6)}
              minTickGap={40}
              tick={{ fill: '#71717a', fontSize: 11 }}
              axisLine={{ stroke: '#27272a' }}
              tickLine={{ stroke: '#27272a' }}
            />
            <YAxis
              yAxisId="primary"
              tickFormatter={(v) => fmtMoneyCompact(v)}
              tick={{ fill: '#71717a', fontSize: 11 }}
              axisLine={{ stroke: '#27272a' }}
              tickLine={{ stroke: '#27272a' }}
              width={56}
              domain={['dataMin - 500', 'dataMax + 500']}
            />
            {showBenchmark && (
              <YAxis
                yAxisId="bench"
                orientation="right"
                tickFormatter={(v) => `${v >= 0 ? '+' : ''}${v.toFixed(0)}%`}
                tick={{ fill: '#52525b', fontSize: 11 }}
                axisLine={{ stroke: '#27272a' }}
                tickLine={{ stroke: '#27272a' }}
                width={42}
                domain={['dataMin - 1', 'dataMax + 1']}
              />
            )}
            <Tooltip
              contentStyle={{
                background: '#0a0a0f',
                border: '1px solid #26262f',
                borderRadius: 8,
                fontSize: 12,
              }}
              labelStyle={{ color: '#a1a1aa' }}
              itemStyle={{ color: '#e4e4e7' }}
              labelFormatter={(v) => fmtDate(String(v))}
              formatter={(v: number, name) => {
                if (name === 'Total value') return [fmtMoney(v), name];
                return [fmtSignedPercent(v), name];
              }}
            />
            {showBenchmark && (
              <Legend
                wrapperStyle={{ fontSize: 11, color: '#71717a' }}
                iconType="line"
                align="right"
                verticalAlign="top"
                height={24}
              />
            )}
            <ReferenceLine
              y={peak}
              yAxisId="primary"
              stroke="#52525b"
              strokeDasharray="3 3"
              strokeOpacity={0.5}
            />
            <Area
              yAxisId="primary"
              type="monotone"
              dataKey="value"
              name="Total value"
              stroke="#818cf8"
              strokeWidth={1.75}
              fill="url(#eqg)"
              isAnimationActive
              animationDuration={400}
            />
            {showBenchmark && (
              <Line
                yAxisId="bench"
                type="monotone"
                dataKey="benchmarkPct"
                name="Synthetic baseline (demo)"
                stroke="#71717a"
                strokeWidth={1.25}
                strokeDasharray="4 3"
                dot={false}
                isAnimationActive={false}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </SectionCard>
  );
}
